import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "modal-edit-room-container" }
const _hoisted_2 = { class: "header" }
const _hoisted_3 = { class: "content" }
const _hoisted_4 = { class: "content" }
const _hoisted_5 = { class: "dropdown-box" }
const _hoisted_6 = { class: "dropdown-box__label" }
const _hoisted_7 = { class: "checkbox-container" }
const _hoisted_8 = { class: "checkbox-container__info" }
const _hoisted_9 = { class: "footer" }

import { useI18n } from 'vue-i18n'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import DropdownStandard, { optionType } from '@/design-system/molecules/DropdownStandard.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import IconInfoCircle from '@/design-system/assets/icons/IconInfoCircle.vue'
import IconXCircleMedium from '@/design-system/assets/icons/IconXCircleMedium.vue'
import TextFieldStandard from '@/design-system/atoms/TextFieldStandard.vue'
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import TooltipStandardV2 from '@/design-system/atoms/TooltipStandardV2.vue'
import { onMounted, ref } from 'vue'
import { COCLASSES_AH } from '@/interfaces/coclass.interface'
import { UpdateCoreEntityDto } from '@/services/core.service'
import { useSelectionStore } from '@/pinia/selection'
import { storeToRefs } from 'pinia'
import { Classification } from '@/interfaces/core.interface'
import { useCoreEntityStore } from '@/pinia/coreEntity'
import { isInteger } from '@/utils/formValidation'
import { useStore } from 'vuex'


export default /*@__PURE__*/_defineComponent({
  __name: 'ModalEditRoom',
  emits: ['closeEditRoomModal'],
  setup(__props, { emit: __emit }) {

const emits = __emit
const { t } = useI18n()
const store = useStore()
const selectionStore = useSelectionStore()
const coreEntityStore = useCoreEntityStore()
const { currentEntity } = storeToRefs(selectionStore)
const roomName = ref<string | undefined>(currentEntity.value?.nameTenant)
const roomLabel = ref<string | undefined>(currentEntity.value?.labelTenant)
const roomCapacity = ref<number | undefined>(currentEntity.value?.properties?.capacity)
const roomUsedType = ref<optionType>()
const selectedRoomType = ref(currentEntity.value?.properties?.classification?.USED?.name)
const stringInput = ref(false)
const dataQualityChecked = ref(false)

const roomUsedTypeOptions = COCLASSES_AH.map((coclass) => ({
  id: coclass.coclass,
  name: coclass.description,
  selected: false,
  disabled: false
})).toSorted((a, b) => a.name.localeCompare(b.name))
function closeModal() {
  emits('closeEditRoomModal', false)
}
function disableSaveButton() {
  if (!formHasChanged()) return true
  return (
    !dataQualityChecked.value ||
    (!roomName.value && !roomLabel.value && !roomCapacity.value && !selectedRoomType.value)
  )
}
function formHasChanged(): boolean {
  return (
    roomName.value !== currentEntity.value?.nameTenant ||
    roomLabel.value !== currentEntity.value?.labelTenant ||
    roomCapacity.value !== currentEntity.value?.properties?.capacity ||
    selectedRoomType.value !== currentEntity.value?.properties?.classification?.USED?.name
  )
}
async function save() {
  if (!currentEntity.value) return
  const classification: Partial<Record<'PLANNED' | 'USED', Classification>> = {}
  if (roomUsedType.value) {
    classification.USED = {
      system: 'CO_CLASS',
      classCode: roomUsedType.value.id,
      name: roomUsedType.value.name
    }
  }
  let requestBody: UpdateCoreEntityDto = {
    type: 'SPACE',
    nameTenant: roomName.value ?? currentEntity.value?.nameTenant,
    labelTenant: roomLabel.value ?? currentEntity.value?.labelTenant,
    properties: {
      ...currentEntity.value?.properties,
      capacity: roomCapacity.value ?? currentEntity.value?.properties?.capacity,
      classification: classification ?? currentEntity.value?.properties?.classification?.USED
    }
  }
  try {
    await coreEntityStore.updateById(currentEntity.value?.id, requestBody)
    store.dispatch('CHANGE_ROOM', { roomId: currentEntity.value?.id })
    store.commit('setInfoMessage', {
      type: 'success',
      message: t('general__text__update-succeded')
    })
    closeModal()
  } catch (error) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: t('general__text__update-failed')
    })
    return
  }
}
function updateDataQuality() {
  if (!dataQualityChecked.value) {
    dataQualityChecked.value = true
    localStorage.setItem('dataQualityChecked', 'true')
  } else {
    dataQualityChecked.value = false
    localStorage.setItem('dataQualityChecked', 'false')
  }
}
function updateName(value: string) {
  if (value.length === 0) {
    roomName.value = currentEntity.value?.nameTenant
  } else {
    roomName.value = value
  }
}
function updateLabel(value: string) {
  if (value.length === 0) {
    roomLabel.value = currentEntity.value?.labelTenant
  } else roomLabel.value = value
}
function updateCapacity(value: string) {
  stringInput.value = !isInteger(value)
  if (stringInput.value) {
    // refactor if enabling sending empty capacity
    roomCapacity.value = currentEntity.value?.properties?.capacity
    return
  }
  roomCapacity.value = parseInt(value)
}
function updateUsedRoomType(value: string) {
  roomUsedType.value = roomUsedTypeOptions.find((option) => option.id === value)
  if (roomUsedType.value) selectedRoomType.value = roomUsedType.value.name
}
onMounted(() => {
  if (localStorage.getItem('dataQualityChecked') === 'true') {
    dataQualityChecked.value = true
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(LayoutOverlay, {
    class: "modal-edit-room",
    onCloseOverlay: _cache[3] || (_cache[3] = ($event: any) => (closeModal()))
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("h2", null, _toDisplayString(_unref(t)('general__text__update') + ' ' + _unref(t)('general__text__information').toLowerCase()), 1),
          _createElementVNode("button", {
            class: "header__close-button",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (closeModal()))
          }, [
            _createVNode(IconXCircleMedium, { class: "header__close-button__icon" })
          ])
        ]),
        _createVNode(DividerHorizontal),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(TextFieldStandard, {
            hideIconX: true,
            infoText: _unref(t)('info__edit-room-name'),
            label: _unref(t)('general__text__room-name'),
            placeHolderText: roomName.value ?? _unref(t)('general__text__room-name'),
            onEmitInput: updateName
          }, null, 8, ["infoText", "label", "placeHolderText"]),
          _createVNode(TextFieldStandard, {
            hideIconX: true,
            infoText: _unref(t)('info__edit-room-number'),
            label: _unref(t)('general__text__room-number'),
            placeHolderText: roomLabel.value ?? _unref(t)('general__text__room-number'),
            onEmitInput: updateLabel
          }, null, 8, ["infoText", "label", "placeHolderText"])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(TextFieldStandard, {
            error: stringInput.value,
            "error-message": _unref(t)('general__text__enter-an-integer'),
            hideIconX: true,
            infoText: _unref(t)('info__edit-room-capacity'),
            label: _unref(t)('general__data__room__capacity-only'),
            placeHolderText: roomCapacity.value?.toString() ?? _unref(t)('general__data__room__capacity-only'),
            onEmitInput: updateCapacity
          }, null, 8, ["error", "error-message", "infoText", "label", "placeHolderText"]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createTextVNode(_toDisplayString(_unref(t)('general__text__used-room-type')) + " ", 1),
              _createVNode(TooltipStandardV2, {
                text: _unref(t)('info__edit-room-type'),
                arrowPosition: 'bottom-center',
                "vertical-offset": 2
              }, {
                default: _withCtx(() => [
                  _createVNode(IconInfoCircle)
                ]),
                _: 1
              }, 8, ["text"])
            ]),
            _createVNode(DropdownStandard, {
              title: selectedRoomType.value ?? _unref(t)('general__text__room-types'),
              hasSearch: false,
              options: _unref(roomUsedTypeOptions),
              onSelect: updateUsedRoomType
            }, null, 8, ["title", "options"])
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(CheckBoxStandard, {
            checked: dataQualityChecked.value,
            title: _unref(t)('info__data-update-quality'),
            disabled: false,
            onValueChanged: updateDataQuality
          }, null, 8, ["checked", "title"]),
          _createElementVNode("div", _hoisted_8, [
            _createVNode(TooltipStandardV2, {
              text: _unref(t)('info__edit-room-quality-update'),
              arrowPosition: 'bottom-center',
              "vertical-offset": 2
            }, {
              default: _withCtx(() => [
                _createVNode(IconInfoCircle)
              ]),
              _: 1
            }, 8, ["text"])
          ])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createVNode(ButtonOutline, {
            class: "button",
            buttonText: _unref(t)('general__text__cancel'),
            size: "small",
            onOnClicked: _cache[1] || (_cache[1] = ($event: any) => (closeModal()))
          }, null, 8, ["buttonText"]),
          _createVNode(ButtonPrimary, {
            class: "button",
            buttonText: _unref(t)('general__text__update-room'),
            disabled: disableSaveButton(),
            size: "small",
            onOnClicked: _cache[2] || (_cache[2] = ($event: any) => (save()))
          }, null, 8, ["buttonText", "disabled"])
        ])
      ])
    ]),
    _: 1
  }))
}
}

})